"use client";

import { useState } from "react";
import { PersonalDataConsent } from "@/components/PersonalDataConsent";

type RentalBookingWidgetProps = {
  product: {
    slug: string;
    sku: string | null;
    name: string;
  };
  priceLabel: string;
  pickupAddress: string;
  rulesHref: string;
  rulesLabel: string;
};

type BookingFieldErrors = Record<string, string[]>;

function getDefaultRentalStartDate() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function RentalBookingWidget({
  product,
  priceLabel,
  pickupAddress,
  rulesHref,
  rulesLabel
}: RentalBookingWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customerType, setCustomerType] = useState<"individual" | "business">("individual");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<BookingFieldErrors>({});
  const [successMessage, setSuccessMessage] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    setFieldErrors({});
    setSuccessMessage("");

    const formData = new FormData(event.currentTarget);
    const payload = {
      productSlug: product.slug,
      customerType,
      contactName: String(formData.get("contactName") ?? ""),
      companyName: String(formData.get("companyName") ?? ""),
      email: String(formData.get("email") ?? ""),
      phone: String(formData.get("phone") ?? ""),
      rentalStartDate: String(formData.get("rentalStartDate") ?? ""),
      rentalDays: Number(formData.get("rentalDays") ?? 1),
      personalDataConsent: formData.get("personalDataConsent") === "1",
      comment: String(formData.get("comment") ?? "")
    };

    try {
      const response = await fetch("/api/rental-bookings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const json = (await response.json()) as {
        ok?: boolean;
        error?: string;
        message?: string;
        fieldErrors?: BookingFieldErrors;
      };

      if (!response.ok) {
        setError(json.error || "Не удалось отправить заявку.");
        setFieldErrors(json.fieldErrors ?? {});
        return;
      }

      event.currentTarget.reset();
      setCustomerType("individual");
      setSuccessMessage(
        json.message || "Заявка отправлена. Мы свяжемся с вами для подтверждения аренды."
      );
    } catch {
      setError("Не удалось отправить заявку. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="rental-booking-widget">
      <button
        className="primary-button buybox-cart-button"
        type="button"
        onClick={() => {
          setIsOpen((current) => !current);
          setError("");
          setFieldErrors({});
        }}
      >
        Забронировать в аренду
      </button>

      <a className="secondary-button buybox-secondary-button" href={rulesHref}>
        {rulesLabel}
      </a>

      {isOpen ? (
        <div className="rental-booking-panel">
          <div className="rental-booking-panel-head">
            <div>
              <strong>Бронирование аренды</strong>
              <span>
                {product.name}
                {product.sku ? ` · ${product.sku}` : ""}
              </span>
            </div>
            <button
              aria-label="Закрыть форму аренды"
              className="rental-booking-close"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="rental-booking-summary">
            <div>
              <span>Тариф</span>
              <strong>{priceLabel}</strong>
            </div>
            <div>
              <span>Выдача</span>
              <strong>{pickupAddress}</strong>
            </div>
          </div>

          {successMessage ? (
            <div className="admin-success rental-booking-success">{successMessage}</div>
          ) : null}
          {error ? <div className="admin-error">{error}</div> : null}

          <form className="rental-booking-form" onSubmit={handleSubmit}>
            <label className="admin-field">
              <span>Тип клиента</span>
              <select
                name="customerType"
                value={customerType}
                onChange={(event) =>
                  setCustomerType(event.target.value as "individual" | "business")
                }
              >
                <option value="individual">Физлицо</option>
                <option value="business">Юрлицо</option>
              </select>
            </label>

            <label className="admin-field">
              <span>ФИО / контактное лицо</span>
              <input name="contactName" required />
              {fieldErrors.contactName?.length ? (
                <em className="field-error">{fieldErrors.contactName[0]}</em>
              ) : null}
            </label>

            {customerType === "business" ? (
              <label className="admin-field">
                <span>Компания</span>
                <input name="companyName" required />
                {fieldErrors.companyName?.length ? (
                  <em className="field-error">{fieldErrors.companyName[0]}</em>
                ) : null}
              </label>
            ) : null}

            <label className="admin-field">
              <span>Телефон</span>
              <input name="phone" required />
              {fieldErrors.phone?.length ? (
                <em className="field-error">{fieldErrors.phone[0]}</em>
              ) : null}
            </label>

            <label className="admin-field">
              <span>Email</span>
              <input name="email" required type="email" />
              {fieldErrors.email?.length ? (
                <em className="field-error">{fieldErrors.email[0]}</em>
              ) : null}
            </label>

            <div className="rental-booking-grid">
              <label className="admin-field">
                <span>Дата начала аренды</span>
                <input
                  defaultValue={getDefaultRentalStartDate()}
                  min={getDefaultRentalStartDate()}
                  name="rentalStartDate"
                  required
                  type="date"
                />
                {fieldErrors.rentalStartDate?.length ? (
                  <em className="field-error">{fieldErrors.rentalStartDate[0]}</em>
                ) : null}
              </label>

              <label className="admin-field">
                <span>Срок аренды, суток</span>
                <input defaultValue="1" min="1" name="rentalDays" required type="number" />
                {fieldErrors.rentalDays?.length ? (
                  <em className="field-error">{fieldErrors.rentalDays[0]}</em>
                ) : null}
              </label>
            </div>

            <label className="admin-field">
              <span>Комментарий</span>
              <textarea
                name="comment"
                placeholder="Уточните время выдачи, объект, документы или другие детали"
                rows={4}
              />
            </label>

            <PersonalDataConsent />
            {fieldErrors.personalDataConsent?.length ? (
              <em className="field-error">{fieldErrors.personalDataConsent[0]}</em>
            ) : null}

            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Отправляем заявку..." : "Отправить заявку"}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
